/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    USER_DELETE_FEATURE, ADMIN_PASSWORD_RESET, ADMIN_DATA_CLEANUP_API, USER_WORKING_DATA_PREVIEW_API
 * Date:         2025-01-13, 2025-01-14, 2025-12-30, 2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Added deleteUser function to support individual user deletion via bulk delete endpoint.
 * Added resetUserPassword function to allow admins to trigger password reset emails for users.
 * Added API service methods for data cleanup operations (orphaned data and working tables).
 * Added preview API method for user working tables.
 *
 * Related Screens / Modules:
 * - client/src/services/adminUsersService.ts
 * - client/src/features/admin/users/UsersFilters.tsx
 * - client/src/features/admin/users/components/DesktopUsersTable.tsx
 */
import axios from "axios";
import { apiUrl } from "../config/environment";
import {
  UserFilters,
  UserListResponse,
  AdminUserUpdateData,
  UserRole,
  UserStatus,
  BulkOperationRequest,
  BulkOperationResponse,
  AdminUser,
} from "../interfaces/adminUsers";
import { formatDateTime } from "../utils/dateFormat";
import { getAuthHeaders } from "../utils/authUtils";

const base = `${apiUrl}/api/users/admin`;

// GET /api/users/admin - List users with pagination, filtering, and search
export function listUsers(
  filters?: UserFilters,
  options?: { signal?: AbortSignal },
): Promise<{ data: UserListResponse<AdminUser> }> {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }

  const url = params.toString() ? `${base}?${params.toString()}` : base;

  return axios.get(url, {
    ...getAuthHeaders(),
    signal: options?.signal,
  });
}

// GET /api/users/admin/export - Export users to CSV
export function exportUsers(filters?: UserFilters): Promise<{ data: Blob }> {
  const params = new URLSearchParams();

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });
  }

  const url = params.toString() ? `${base}/export?${params.toString()}` : `${base}/export`;

  return axios.get(url, {
    ...getAuthHeaders(),
    responseType: "blob",
  });
}

// GET /api/users/admin/:id - Get user by ID (admin version)
export function getUser(id: string): Promise<{ data: AdminUser }> {
  return axios.get(`${base}/${id}`, getAuthHeaders());
}

// PUT /api/users/admin/:id - Admin update user
export function updateUser(id: string, payload: AdminUserUpdateData): Promise<{ data: AdminUser }> {
  return axios.put(`${base}/${id}`, payload, getAuthHeaders());
}

// PATCH /api/users/admin/:id/role - Change user role
export function changeUserRole(id: string, role: UserRole): Promise<{ data: AdminUser }> {
  return axios.patch(`${base}/${id}/role`, { role }, getAuthHeaders());
}

// PATCH /api/users/admin/:id/status - Change user status
export function setUserStatus(id: string, status: UserStatus): Promise<{ data: AdminUser }> {
  return axios.patch(`${base}/${id}/status`, { status }, getAuthHeaders());
}

// POST /api/users/admin/bulk - Bulk operations
export function bulkUsers(
  ids: string[],
  op: "delete" | "setRole" | "setStatus",
  payload?: { role?: UserRole; status?: UserStatus },
): Promise<{ data: BulkOperationResponse }> {
  const requestBody: BulkOperationRequest = { ids, op };
  if (payload) {
    requestBody.payload = payload;
  }

  return axios.post(`${base}/bulk`, requestBody, getAuthHeaders());
}

/**
 * Delete a user (soft delete - sets deletedAt field)
 * Uses the bulk delete endpoint with a single user ID
 */
export async function deleteUser(userId: string): Promise<void> {
  // [2025-01-13] user-delete: use bulk delete endpoint with single ID for soft delete
  const response = await bulkUsers([userId], "delete");
  if (response.data.modifiedCount === 0) {
    throw new Error("User not found or already deleted");
  }
}

/**
 * POST /api/users/admin/:id/reset-password
 * Trigger password reset for user (admin action)
 * Sends password reset email to user
 */
export async function resetUserPassword(userId: string): Promise<void> {
  // [2025-01-14] Admin password reset: trigger password reset email for user
  await axios.post(`${base}/${userId}/reset-password`, {}, getAuthHeaders());
}

// [2026-06-23] impersonation: start "View as User" (read-only). Server stashes the
// admin token and sets a short-lived user token cookie. Caller should full-reload after.
export async function startImpersonation(
  userId: string,
): Promise<{ id: string; firstName: string; lastName: string; email: string }> {
  const response = await axios.post(`${base}/${userId}/impersonate`, {}, getAuthHeaders());
  return response.data.impersonating;
}

// [2026-06-23] impersonation: stop "View as User" and restore the admin session
export async function stopImpersonation(): Promise<void> {
  await axios.post(`${base}/stop-impersonate`, {}, getAuthHeaders());
}

// [2025-12-30] Data cleanup: Interface for orphaned data cleanup results
export interface OrphanedDataCleanupResult {
  totalOrphaned: number;
  collectionsProcessed: number;
  collectionsWithOrphans: number;
  [collectionName: string]: number; // Dynamic collection names
}

// [2025-12-30] Data cleanup: Interface for working tables cleanup results
export interface WorkingTablesCleanupResult {
  totalDeleted: number;
  userName: string;
  userEmail: string;
  [collectionName: string]: number | string; // Dynamic collection names
}

// [2025-12-30] Data cleanup: Preview orphaned user data (read-only)
export async function previewOrphanedData(): Promise<OrphanedDataCleanupResult> {
  const response = await axios.get(`${base}/orphaned-data/preview`, getAuthHeaders());
  return response.data.data;
}

// [2025-12-30] Data cleanup: Delete orphaned user data
export async function deleteOrphanedData(): Promise<OrphanedDataCleanupResult> {
  const response = await axios.delete(`${base}/orphaned-data`, getAuthHeaders());
  return response.data.data;
}

// [2025-12-30] Data cleanup: Preview user working tables (read-only)
export async function previewUserWorkingTables(userId: string): Promise<WorkingTablesCleanupResult> {
  const response = await axios.get(`${base}/${userId}/working-tables/preview`, getAuthHeaders());
  return response.data.data;
}

// [2025-12-30] Data cleanup: Delete user working tables
export async function deleteUserWorkingTables(userId: string): Promise<WorkingTablesCleanupResult> {
  const response = await axios.delete(`${base}/${userId}/working-tables`, getAuthHeaders());
  return response.data.data;
}

// Helper function to download CSV file
export function downloadCSV(blob: Blob, filename?: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || `users-export-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Helper function to validate user filters
export function validateUserFilters(filters: UserFilters): string[] {
  const errors: string[] = [];

  if (filters.page && filters.page < 1) {
    errors.push("Page must be greater than 0");
  }

  if (filters.limit && (filters.limit < 1 || filters.limit > 100)) {
    errors.push("Limit must be between 1 and 100");
  }

  if (filters.role && !["admin", "user"].includes(filters.role)) {
    errors.push("Role must be 'admin' or 'user'");
  }

  if (filters.status && !["active", "suspended", "pending"].includes(filters.status)) {
    errors.push("Status must be 'active', 'suspended', or 'pending'");
  }

  if (filters.verified && !["1", "0"].includes(filters.verified)) {
    errors.push("Verified must be '1' or '0'");
  }

  if (filters.sortOrder && !["asc", "desc"].includes(filters.sortOrder)) {
    errors.push("Sort order must be 'asc' or 'desc'");
  }

  if (filters.dateFrom && filters.dateTo) {
    const fromDate = new Date(filters.dateFrom);
    const toDate = new Date(filters.dateTo);
    if (fromDate > toDate) {
      errors.push("Date from must be before date to");
    }
  }

  return errors;
}

// Helper function to format user for display
export function formatUserForDisplay(
  user: AdminUser,
  country?: string | null,
): {
  displayName: string;
  displayRole: string;
  displayStatus: string;
  displayVerified: string;
  displayLastLogin: string;
} {
  return {
    displayName: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unknown",
    displayRole: user.isAdmin ? "Admin" : "User",
    displayStatus: user.status
      ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
      : "Active",
    displayVerified: user.email_verified === "Y" ? "Verified" : "Unverified",
    displayLastLogin: user.lastLogin ? formatDateTime(user.lastLogin, country) : "Never",
  };
}
